// Design System Surface Tokens
// Defines surface colors, elevations, and patterns for consistent UI hierarchy

export const SURFACE_TOKENS = {
  // Surface Colors - for panels, cards, and containers
  surfaces: {
    // Chat Panel Surfaces
    chatPanel: {
      header: {
        primary: '#FFFFFF',
        secondary: '#F8FAFC',
        accent: '#F1F5F9',
      },
      body: {
        primary: '#FFFFFF',
        secondary: '#F8FAFC',
        elevated: '#F1F5F9',
      },
      divider: '#E2E8F0',
    },
    
    // Navigation Surfaces
    navigation: {
      primary: '#FFFFFF',
      secondary: '#F8FAFC',
      hover: '#F1F5F9',
      active: '#E2E8F0',
    },
    
    // Content Surfaces
    content: {
      primary: '#FFFFFF',
      secondary: '#FAFBFC',
      tertiary: '#F4F6F8',
      overlay: 'rgba(15, 23, 42, 0.1)',
    },
  },
  
  // Elevation Shadows
  elevation: {
    none: 'none',
    subtle: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    small: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
    medium: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    large: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xlarge: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  
  // Border Patterns
  borders: {
    subtle: '1px solid #F1F5F9',
    default: '1px solid #E2E8F0',
    strong: '1px solid #CBD5E1',
    accent: '1px solid #94A3B8',
  },
  
  // Border Radius Patterns
  borderRadius: {
    none: '0',
    sm: '0.125rem',
    md: '0.375rem',
    lg: '0.5rem',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    full: '9999px',
  },
} as const;

// Chat-specific surface patterns
export const CHAT_SURFACE_PATTERNS = {
  // Header patterns for better hierarchy
  header: {
    primary: {
      backgroundColor: SURFACE_TOKENS.surfaces.chatPanel.header.primary,
      borderBottom: SURFACE_TOKENS.borders.default,
      boxShadow: SURFACE_TOKENS.elevation.subtle,
    },
    secondary: {
      backgroundColor: SURFACE_TOKENS.surfaces.chatPanel.header.secondary,
      borderBottom: SURFACE_TOKENS.borders.subtle,
    },
  },
  
  // List item patterns
  listItem: {
    default: {
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: SURFACE_TOKENS.surfaces.navigation.hover,
      },
    },
    hover: {
      backgroundColor: SURFACE_TOKENS.surfaces.navigation.hover,
      transition: 'background-color 0.2s ease-in-out',
    },
    active: {
      backgroundColor: SURFACE_TOKENS.surfaces.navigation.active,
      borderLeft: '3px solid #84CC16',
    },
  },
  
  // Welcome section patterns
  welcomeSection: {
    default: {
      backgroundColor: SURFACE_TOKENS.surfaces.content.secondary,
      border: SURFACE_TOKENS.borders.subtle,
      borderRadius: '12px',
      boxShadow: SURFACE_TOKENS.elevation.small,
    },
  },
  
  // Chip patterns for consistent styling
  chip: {
    feature: {
      fontSize: '0.75rem',
      fontWeight: 500,
      borderColor: SURFACE_TOKENS.borders.subtle,
      color: '#64748B',
      backgroundColor: SURFACE_TOKENS.surfaces.content.primary,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: SURFACE_TOKENS.surfaces.navigation.hover,
        borderColor: '#84CC16',
      },
      '& .MuiChip-icon': {
        color: '#84CC16',
        fontSize: '1rem',
      },
    },
  },
} as const;

export default SURFACE_TOKENS;